import { ImageWithFallback } from "@/components/image/ImageWithFallback";
import { BottomSheet } from "@/components/modals/BottomSheet";
import {
  Button,
  Card,
  DescriptionList,
  KeyValueRow,
  Price,
  SheetBody,
  SheetFooter,
  Text,
} from "@/components/ui";
import { formatBRL } from "@/lib/format-brl";
import type { PriceReview, PriceReviewLine } from "@/types/external";

/** Cents on the wire; reais on screen. */
function fromMinor(minor: number): number {
  return minor / 100;
}

function LineRow({ line }: { line: PriceReviewLine }) {
  const delta = line.currentTotalMinor - line.agreedTotalMinor;
  const gotPricier = delta > 0;

  return (
    <li className="flex items-center gap-3 py-3">
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-card bg-gray-scale-100">
        <ImageWithFallback
          width={56}
          height={56}
          alt={line.name}
          src={line.imageUrl}
          fit="contain"
          className="h-full w-full"
          fallbackClassName="h-full w-full"
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <Text as="span" size="base" weight="semibold" tone="default" truncate>
          {line.name}
        </Text>
        <Text as="span" size="sm" tone="muted">
          {line.quantity} {line.unit || "un"}
        </Text>
      </div>

      <div className="flex shrink-0 flex-col items-end">
        <Price value={fromMinor(line.agreedTotalMinor)} strike size="sm" />
        <Price
          value={fromMinor(line.currentTotalMinor)}
          size="base"
          weight="bold"
          tone={gotPricier ? "danger" : "secondary"}
        />
      </div>
    </li>
  );
}

export type PriceReviewSheetProps = {
  open: boolean;
  review: PriceReview | null;
  /** True while the decision is in flight, so the buttons cannot double-fire. */
  submitting?: boolean;
  onAccept: () => void;
  onDecline: () => void;
};

/**
 * The decision the customer takes when the store repriced something between
 * building the bag and checking out.
 *
 * A sheet rather than an inline banner, and non-dismissible, for the same
 * reason the PIX QR is one: the saga is parked waiting on this answer, and
 * closing it by accident would leave the order hanging until the timeout.
 */
export function PriceReviewSheet({
  open,
  review,
  submitting = false,
  onAccept,
  onDecline,
}: PriceReviewSheetProps) {
  if (!review) return null;

  const delta = review.currentSubtotalMinor - review.agreedSubtotalMinor;
  const many = review.lines.length > 1;

  return (
    <BottomSheet
      open={open}
      onOpenChange={() => undefined}
      title={many ? "Alguns itens mudaram de preço" : "Um item mudou de preço"}
      description="A loja atualizou o cardápio enquanto você montava a sacola. Confira o que mudou e escolha como seguir."
      contentWidth="narrow"
    >
      <SheetBody>
        <ul className="divide-y divide-border">
          {review.lines.map((line) => (
            <LineRow key={line.merchantItemId} line={line} />
          ))}
        </ul>

        <Card variant="tinted" padding="md" className="mt-4">
          <DescriptionList>
            <KeyValueRow
              label="Total anterior"
              value={<Price value={fromMinor(review.agreedSubtotalMinor)} strike tone="muted" />}
            />
            <KeyValueRow
              label="Novo total"
              emphasis="total"
              value={<Price value={fromMinor(review.currentSubtotalMinor)} />}
            />
          </DescriptionList>
          {delta !== 0 && (
            <Text size="sm" tone="muted" className="mt-1">
              {delta > 0
                ? `São ${formatBRL(fromMinor(delta))} a mais que o previsto.`
                : `São ${formatBRL(fromMinor(Math.abs(delta)))} a menos que o previsto.`}
            </Text>
          )}
        </Card>
      </SheetBody>

      <SheetFooter className="flex flex-col gap-2 pt-5">
        <Button
          fullWidth
          size="lg"
          isLoading={submitting}
          onClick={onAccept}
          data-testid="price-review-accept"
        >
          {submitting ? "Confirmando..." : "Continuar com o novo valor"}
        </Button>
        <Button
          variant="outline"
          tone="neutral"
          fullWidth
          size="lg"
          disabled={submitting}
          onClick={onDecline}
          data-testid="price-review-decline"
        >
          Voltar para a sacola
        </Button>
      </SheetFooter>
    </BottomSheet>
  );
}
